import { useMemo, useState } from 'react';
import type { ColumnDirection } from '@hotjar/hotsauce';
import { useTranslation, formatPercentage } from '@hotjar/translations';
import { Table } from '@hotjar/hotsauce';
import { useNavigation } from '@hotjar/navigation';
import type { AggregatedResult } from '../../../../domain/Surveys/Results/AggregatedResult';
import {
  toLetter,
  compareString,
  sortWithFallback,
  compareNumber,
  useHighestValue,
  SmallHeadCell,
  StyledRow,
  SmallCell,
  CenteredCell,
} from './AnswersTable';

type StringColumnKey = 'question' | 'answer';
type NumberColumnKey = 'count' | 'percentRespondents' | 'percentAnswers';

type RowData = Record<StringColumnKey, string> & Record<NumberColumnKey, number>;

type ColumnKey = StringColumnKey | NumberColumnKey;

const toRowData = (result: AggregatedResult): RowData[] => {
  return Object.keys(result.answers).map((key, index) => {
    return {
      question: toLetter(index),
      answer: key,
      count: result.answers[key],
      percentAnswers: result.pc_answers[key],
      percentRespondents: (result.answers[key] / result.total) * 100,
    };
  });
};

export interface MultiAnswersTableProps {
  result: AggregatedResult;
  showIndex: boolean;
  isSticky?: boolean;
}

interface SortState {
  key: ColumnKey;
  direction: ColumnDirection;
}

const getCompare = (key: ColumnKey, direction: ColumnDirection) => {
  // Fallback to question sort to ensure stable ordering
  const questionSort = (a: RowData, b: RowData, sortDirection: ColumnDirection) =>
    compareString(a.question, b.question, sortDirection);

  switch (key) {
    case 'question':
      return (a: RowData, b: RowData) => questionSort(a, b, direction);
    case 'answer':
      return (rowA: RowData, rowB: RowData) =>
        sortWithFallback(
          rowA,
          rowB,
          direction,
          (a, b, sortDirection) => compareString(a[key], b[key], sortDirection),
          questionSort,
        );
    case 'count':
    case 'percentRespondents':
    case 'percentAnswers':
      return (rowA: RowData, rowB: RowData) =>
        sortWithFallback(
          rowA,
          rowB,
          direction,
          (a, b, sortDirection) => compareNumber(a[key], b[key], sortDirection),
          questionSort,
        );
    default:
      throw new Error('Unexpected key');
  }
};

export const MultiAnswersTable = ({
  result,
  showIndex,
  isSticky = true,
}: MultiAnswersTableProps): JSX.Element => {
  const { t, i18n } = useTranslation('feedback');

  const [sortState, setSortState] = useState<SortState>({ key: 'question', direction: 'asc' });
  const rowData = useMemo(
    () => toRowData(result).sort(getCompare(sortState.key, sortState.direction)),
    [result, sortState],
  );

  const highestValue = useHighestValue(result);

  const { topBarHeight: topNavHeight } = useNavigation();

  const getSortDirection = (columnKey: ColumnKey) =>
    sortState.key === columnKey ? sortState.direction : 'none';

  const getSort = (columnKey: ColumnKey) => {
    return (newDirection: ColumnDirection) =>
      setSortState({ key: columnKey, direction: newDirection });
  };

  const caption = t('surveys.multi-answer-table.caption', {
    count: result.total,
    answers: t('surveys.multi-answer-table.answers', { count: result.num_answers }),
  });

  return (
    <Table
      caption={caption}
      tableName={t('surveys.answer-table.name')}
      withStickyLastColumn={false}
    >
      <Table.Head size="m">
        <Table.HeadRow isSticky={isSticky} stickyOffset={topNavHeight}>
          {showIndex && (
            <SmallHeadCell
              isSortable
              title={t('surveys.answer-table.question')}
              sortDirection={getSortDirection('question')}
              onSort={getSort('question')}
            >
              {t('common.number-sign')}
            </SmallHeadCell>
          )}
          <Table.HeadCell
            isSortable
            title={t('surveys.answer-table.answer')}
            sortDirection={getSortDirection('answer')}
            onSort={getSort('answer')}
          >
            {t('surveys.answer-table.answer')}
          </Table.HeadCell>
          <SmallHeadCell
            isSortable
            title={t('surveys.answer-table.count')}
            sortDirection={getSortDirection('count')}
            onSort={getSort('count')}
          >
            {t('surveys.answer-table.count')}
          </SmallHeadCell>
          <SmallHeadCell
            isSortable
            title={t('surveys.answer-table.percentage-of-respondents')}
            sortDirection={getSortDirection('percentRespondents')}
            onSort={getSort('percentRespondents')}
          >
            {t('surveys.answer-table.percentage-of-respondents-short')}
          </SmallHeadCell>
          <SmallHeadCell
            isSortable
            title={t('surveys.answer-table.percentage-of-answers')}
            sortDirection={getSortDirection('percentAnswers')}
            onSort={getSort('percentAnswers')}
          >
            {t('surveys.answer-table.percentage-of-answers-short')}
          </SmallHeadCell>
        </Table.HeadRow>
      </Table.Head>
      <Table.Body>
        <>
          {rowData.map((row) => {
            return (
              <StyledRow
                key={row.question}
                isHighlighted={highestValue === row.count}
                aria-label={
                  highestValue === row.count ? t('surveys.answer-table.most-frequent') : undefined
                }
              >
                <>
                  {showIndex && <SmallCell>{row.question}</SmallCell>}
                  <Table.Cell>{row.answer}</Table.Cell>
                  <SmallCell>{row.count.toLocaleString(i18n.language)}</SmallCell>
                  <SmallCell>
                    {formatPercentage(Math.round(row.percentRespondents), i18n)}
                  </SmallCell>
                  <SmallCell>{row.percentAnswers}%</SmallCell>
                </>
              </StyledRow>
            );
          })}
        </>
      </Table.Body>
      <Table.Foot>
        <StyledRow>
          <CenteredCell colSpan={showIndex ? 5 : 4}>{caption}</CenteredCell>
        </StyledRow>
      </Table.Foot>
    </Table>
  );
};
